/**
 * A failure message an admin can act on, from the one the system actually threw.
 *
 * Every run stores its error VERBATIM and that never changes — this translates at DISPLAY
 * time, and the original is always shown beside the translation in the run's detail. Two
 * reasons it works that way round rather than rewriting on the way into the database:
 *
 *   It is reversible. A translation that is wrong, or missing, still has the real message
 *   behind it. Rewrite on write and the original is gone for good.
 *
 *   It applies backwards. Improving the wording tomorrow improves every row already stored,
 *   including the ones that failed before this file existed.
 *
 * WHAT A GOOD ENTRY LOOKS LIKE. Not a nicer paraphrase of the error — a statement of what it
 * means for the data, and what to do. "HTTP 401" becomes "the key was rejected, so nothing
 * was read, and it needs replacing in Retailer Accounts". An admin reading it should know
 * whether to wait, to fix something, or to call someone.
 *
 * Every pattern below is a failure that has actually happened in this install, taken from the
 * stored rows rather than imagined. An unmatched error is NOT dressed up: `known` comes back
 * false and the caller shows the original, because a confident wrong summary over a real
 * error is worse than the raw text.
 */

/**
 * Ordered, and the order matters: the first match wins, so anything specific must come before
 * anything general. A bare /401/ placed above the Shopify token case would swallow it.
 */
const PATTERNS = [
  {
    // "Shopify access token for x.myshopify.com has expired and could not be refreshed."
    test: /access token .* (has )?expired|refresh token was rejected/i,
    summary: 'Shopify signed 2W out of this store.',
    detail: 'The app\'s access expired and could not renew itself, so nothing could be read '
      + 'or written for this brand. Someone needs to open the 2W app once in that store\'s '
      + 'Shopify admin to re-authorise it. Nothing is lost — the run will work again after '
      + 'that.',
  },
  {
    // "Access denied for customer field. Required access: `read_customers` access scope."
    test: /access denied for customer field|read_customers/i,
    summary: 'Shopify is withholding customer details from 2W.',
    detail: 'The app has not been granted permission to read customers in this store, so the '
      + 'run could not fetch the name and address an order needs. The permission is granted '
      + 'by reinstalling or re-approving the app in Shopify admin. Orders already stored are '
      + 'unaffected.',
  },
  {
    // "[API] Invalid API key or access token (unrecognized login or wrong password)"
    test: /invalid api key|unrecognized login|wrong password/i,
    summary: 'The retailer rejected our key.',
    detail: 'The retailer refused the credentials 2W holds for this account, so nothing was '
      + 'read or sent. Nothing has changed at the retailer. The key needs checking and '
      + 'replacing on the account under Retailer Accounts.',
  },
  {
    // A stricter 401 than the one above, for anything that did not name a cause.
    test: /\b401\b|unauthorized/i,
    summary: 'The retailer refused the request as unauthorised.',
    detail: 'The account\'s credentials were not accepted, so nothing was read or sent and '
      + 'nothing changed at the retailer. Check the key on that account under Retailer '
      + 'Accounts; if it looks right, the retailer may have revoked it at their end.',
  },
  {
    // "Invalid `prisma.miraklOfferPush.updateMany()` invocation ... Expected Int ... provided String"
    test: /invalid `prisma\.|prisma\.\w+\.\w+\(\) invocation/i,
    summary: '2W could not save the result of this run.',
    detail: 'The work itself may well have reached the retailer or Shopify — what failed was '
      + 'recording the outcome here. That makes 2W\'s records incomplete rather than the '
      + 'other side wrong, and it usually repeats every run until it is fixed in the code. '
      + 'Worth reporting with the technical detail below.',
  },
  {
    // "GraphQL errors: syntax error, unexpected invalid token ..."
    test: /graphql errors?:.*syntax error/i,
    summary: '2W sent Shopify a request it could not read.',
    detail: 'Shopify rejected the request as malformed, so nothing was read and nothing was '
      + 'changed. This is a fault in 2W rather than anything about the store or its data, and '
      + 'it will repeat until the code is fixed. Worth reporting with the technical detail '
      + 'below.',
  },
  {
    test: /graphql errors?:/i,
    summary: 'Shopify refused this request.',
    detail: 'The run could not read what it needed from the store, so it stopped. Nothing was '
      + 'changed in Shopify. The technical detail below carries Shopify\'s own reason.',
  },
  {
    test: /econnrefused|enotfound|etimedout|socket hang up|network|fetch failed/i,
    summary: 'We could not reach the other side.',
    detail: 'The connection failed before anything was sent, so nothing changed anywhere. '
      + 'This is usually temporary and the next scheduled run will pick it up — a run that '
      + 'keeps failing this way points at the retailer being down or unreachable from here.',
  },
  {
    test: /\b5\d\d\b|internal server error|bad gateway|service unavailable/i,
    summary: 'The other side returned an error of its own.',
    detail: 'The retailer or Shopify failed to answer properly. Whether anything reached them '
      + 'is not knowable from here, so check the figures before re-running. This is usually '
      + 'temporary.',
  },
  {
    test: /rate limit|too many requests|\b429\b/i,
    summary: 'We asked the other side too often.',
    detail: 'The request was refused for going over a rate limit, so nothing was read or '
      + 'changed. The next scheduled run will pick it up. If it keeps happening, the interval '
      + 'for this job in Sync Settings is too tight.',
  },
];

/**
 * A raw failure message, explained.
 *
 * Returns `{ known, summary, detail, raw }`. When `known` is false the summary IS the raw
 * text: an unrecognised error is shown as thrown rather than flattened into "something went
 * wrong", which would take away the one thing a reader could otherwise search for.
 */
export const friendlyError = (raw) => {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) return { known: false, summary: null, detail: null, raw: null };

  const hit = PATTERNS.find((p) => p.test.test(text));
  if (!hit) return { known: false, summary: text, detail: null, raw: text };

  return { known: true, summary: hit.summary, detail: hit.detail, raw: text };
};

/** The one line a table cell shows. Falls back to the original when nothing matched. */
export const errorSummary = (raw) => friendlyError(raw).summary;
